import { Box, ButtonBase, Stack, Typography } from '@mui/material'
import moment from 'moment'
import { FC } from 'react'

import { CustomModal } from '../../../components/common/CustomModal'
import { CourtDateTodayChip } from './CourtDateTodayChip'
import {
  COURT_DATE_TIMING_STYLES,
  CourtDateEvent,
  getCourtDateTiming,
  getMatterNumber,
  getMatterStatusStyle,
  PAST_COURT_DATE_OPACITY,
  sortByStart
} from './courtDatesUtils'

export interface CourtDateList {
  title: string
  events: CourtDateEvent[]
}

interface CourtDateListModalProps {
  list: CourtDateList | null
  onSelect: (event: CourtDateEvent) => void
  onClose: () => void
}

/**
 * The court dates behind a "+N more" link, in every view. Always outlined (a list of filled cards is
 * heavy), with past ones faded as on the calendar. Picking one opens its details on top of the list.
 */
export const CourtDateListModal: FC<CourtDateListModalProps> = ({ list, onSelect, onClose }) => {
  if (!list) return null

  return (
    <CustomModal isOpen={!!list} handleClose={onClose} title={list.title} maxWidth="sm">
      <Stack spacing={1}>
        {sortByStart(list.events).map(event => {
          const { courtDate } = event
          const timing = getCourtDateTiming(event)
          const { color } = COURT_DATE_TIMING_STYLES[timing]
          const { Icon } = getMatterStatusStyle(courtDate.caseStatus)
          const matterNumber = getMatterNumber(courtDate)
          const details = [
            `${moment(event.start).format('h:mm A')} – ${moment(event.end).format('h:mm A')}`,
            courtDate.clientName,
            courtDate.courtName
          ].filter(Boolean)

          return (
            <ButtonBase
              key={event.id}
              onClick={() => onSelect(event)}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                gap: 1,
                width: '100%',
                p: 1.5,
                textAlign: 'left',
                border: `1px solid ${color}`,
                borderRadius: '4px',
                opacity: timing === 'past' ? PAST_COURT_DATE_OPACITY : 1,
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <Icon sx={{ fontSize: '18px', color, mt: '2px', flexShrink: 0 }} />

              <Box minWidth={0} flex={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontSize="14px" fontWeight={500} color="secondary.main" sx={{ overflowWrap: 'anywhere' }}>
                    {matterNumber ? `${matterNumber} · ${event.title}` : event.title}
                  </Typography>
                  {timing === 'today' && <CourtDateTodayChip />}
                </Box>
                <Typography fontSize="13px" color="lightGrayOne" sx={{ overflowWrap: 'anywhere' }}>
                  {details.join(' · ')}
                </Typography>
              </Box>
            </ButtonBase>
          )
        })}
      </Stack>
    </CustomModal>
  )
}
